import { Component, Input } from '@angular/core';
import { DownloadListModalOptions } from '@app/lists/model/download-list-options.model';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { BaseDualPaneModalContainerComponent } from '@shared/components/modal-with-nav-inline/dual-pane-container/base-dual-pane-modal-container/base-dual-pane-modal-container.component';
import {
  dualPaneDefaultVerifySubmit,
  getSearchableObservable$,
  singleSelectRadioSearchDefault,
  updateRightPaneWithValue,
} from '@shared/components/modal-with-nav-inline/dual-pane-container/Helpers/dual-pane-helper-functions';
import {
  DualPaneView,
  MultiSelectItems,
} from '@shared/constants/dual-pane.enum';
import { ListTypes } from '@shared/constants/lists-constants';
import {
  BasicListItem,
  DualPaneFlagsConfig,
  DualPaneFunctionsConfig,
  DualPaneLabelsConfig,
  DualPaneViewModel,
  LeftPaneInfo,
  RightPaneInfo,
} from '@shared/models/dual-pane-model';
import { DownloadRequestFileType } from '@usf/list-types';
import { DownloadListOptions } from '@usf/ngrx-list';
import { BehaviorSubject, of } from 'rxjs';

@Component({
  selector: 'app-list-print-container',
  templateUrl: './list-print-container.component.html',
  styleUrls: ['./list-print-container.component.scss'],
})
export class ListPrintContainerComponent extends BaseDualPaneModalContainerComponent {
  @Input() translateService: TranslateService;

  fileName: string;
  format: DownloadRequestFileType;
  listName: string;
  listId: number;
  listTypeId: string;
  isDownload: boolean;
  customerNumber: number;
  printOptions = [];

  constructor(
    public platformService: PlatformService,
    readonly panAppState: PanAppState,
  ) {
    super(platformService);
  }

  createListPrintVM = () => {
    this.setupPrintOptions();
    const labelConfig: DualPaneLabelsConfig = {
      dataCy: 'list-print-modal',
      title: this.translateService.instant('i18n.lists.printListModal.title', {
        listName: this.listName,
      }),
      htmlSubTitle: this.translateService.instant(
        'i18n.lists.printListModal.downloadStatement',
      ),
      submitText: this.translateService.instant('i18n.common.print'),
      backText: this.translateService.instant('i18n.common.save'),
    };
    const functionConfig: DualPaneFunctionsConfig = {
      intialDesktopTabletView: this.buildListOptions,
      handleSubmit: this.submitModalValues,
      verifySubmit: () => true,
    };

    const flagConfig: DualPaneFlagsConfig = {};
    const contentItems = [
      {
        label: 'i18n.lists.printListModal.printOptions',
        placeHolder: 'i18n.lists.printListModal.chooseOptions',
        view: DualPaneView.searchableItemRadio,
        selected: true,
        onSelectfunction: this.buildListOptions,
      },
    ] as BasicListItem[];

    this.buildBasePaneWithConfigs(
      labelConfig,
      functionConfig,
      flagConfig,
      contentItems,
    );
    this.dualPaneModalData.viewModel.disableSubmit = false;
  };

  setupPrintOptions = () => {
    this.printOptions = [];

    this.printOptions.push({
      label: 'i18n.lists.downloadListModal.includePrice',
      value: 'price',
      selected: false,
      type: MultiSelectItems.basic,
    });

    if (this.listTypeId !== ListTypes.recentlyPurchased) {
      this.printOptions.push({
        label: 'i18n.lists.downloadListModal.includeNote',
        value: 'note',
        selected: false,
        type: MultiSelectItems.basic,
      });
    }
    this.printOptions.push({
      label: 'i18n.lists.downloadListModal.includeStatus',
      value: 'status',
      selected: false,
      type: MultiSelectItems.basic,
    });
    this.printOptions.push({
      label: 'i18n.lists.downloadListModal.masterListAssociation',
      value: 'masterListAssociation',
      selected: false,
      type: MultiSelectItems.basic,
    });
  };

  buildListOptions = (viewModel: DualPaneViewModel, paneSelected: string) => {
    const excludeModal = {
      view: DualPaneView.multiSelectCheckbox,
      title: 'i18n.lists.printListModal.printOptions',
      verifySaveButton: (vm: any) => true,
      searchPane: {
        hideSearch: true,
        onSearchFunction: singleSelectRadioSearchDefault,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
        searchPlaceholder: '',
        searchTitle: '',
      },
      countPane: {
        primaryCount: 0,
        hideSelectAll: true,
        selectableText: '',
      },
      itemsObservable$: of([...this.printOptions]),
      handleItemSelection: this.excludeClassSelection,
      overrideSaveOnNavigation: this.saveOverrideExclude,
    } as RightPaneInfo;
    excludeModal.searchPane.searchableObservable$ =
      getSearchableObservable$(excludeModal);
    updateRightPaneWithValue(viewModel, paneSelected, excludeModal);
    return excludeModal;
  };

  saveOverrideExclude = (
    vm: DualPaneViewModel,
    item: BasicListItem,
    value: any,
  ) => {
    item.value = value;
    item.subLabel =
      value.length > 0
        ? this.translateService.instant('i18n.lists.massDeleteModal.selected') +
          value.length
        : '';
    return item;
  };

  excludeClassSelection = (
    left: LeftPaneInfo,
    platform: PlatformEnum,
    right: RightPaneInfo,
    value: any,
    selectedCount: string,
  ) => {
    left.content[0].subLabel = value.length > 0 ? selectedCount : '';
    left.content[0].value = value;
    right.value = value;
  };

  submitModalValues = async (vm: DualPaneViewModel) => {
    const submitMap = new Map<string, any | any[]>();

    const options = vm.leftView.content[0].value;
    if (!!options) {
      options.forEach(element => {
        submitMap.set(element.value, true);
      });
    }
    submitMap.set('type', this.listTypeId);
    submitMap.set('id', this.listId);
    submitMap.set('listName', this.listName);
    submitMap.set('download', this.isDownload);
    this.submit$.next(submitMap);
    this.closeModalAndSubmit();
  };

  async openModal(options: DownloadListModalOptions) {
    this.listName = options.listName;
    this.listId = options.listId;
    this.listTypeId = options.listTypeId;
    this.isDownload = options.isDownload;
    this.createListPrintVM();
    this.isModalOpen$.next(true);
  }

  closeModal = () => {
    this.isModalOpen$.next(false);
  };

  closeModalAndSubmit() {
    this.isModalOpen$.next(false);
    if (this.submit$.value !== undefined) {
      const submitMap = this.submit$.value;
      console.log('submitMap:\n', submitMap);
      const options = {
        fileName: this.listName,
        format: 'PDF',
        includeNutritionals: false,
        includeProductPrices: !!submitMap.get('price'),
        includeProductNotes: !!submitMap.get('note'),
        includeProductType: !!submitMap.get('status'),
        includeProductStatus: !!submitMap.get('status'),
        includeMasterListAssociation: !!submitMap.get('masterListAssociation'),
        listTypeId: submitMap.get('type'),
        listId: submitMap.get('id'),
        listName: submitMap.get('listName'),
        isDownload: false,
      } as DownloadListOptions;
      this.submitEvent.emit(options);
    }
  }
}
